import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import {
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_NONE,
} from "@/v2-backoffice-client/license/license.constants"

export type LicenseGoverningBadgeProps = Omit<BadgeProps, "variant"> & {
  /**
   * The governing license posture of a customer, as emitted by the
   * licenseFacts blocks in the board procs: LicenseStatusCode 1-6 =
   * lic.LicenseStatuses, 0 = no license held, null/undefined = license
   * tracking does not apply to the row (e.g. a user without a customer).
   * The aggregate facts are optional — reads that carry them (the customer
   * boards and panel) get the end stamp and the hover details for free;
   * thinner reads (user rows) still render the plain pill.
   */
  license: {
    LicenseStatusCode?: number | null
    LicenseTrialEndsAt?: string | null
    LicensePlanNames?: string | null
    LicensedSeats?: number | null
    LicenseRenewsAt?: string | null
    LicenseEndedAt?: string | null
    OwnerName?: string | null
  }
  /** Status display name from the getLicenseStatuses lookup. */
  statusName?: string
}

// The governing-license pill shared by the users and customers boards (and
// their panels). Renders nothing when license tracking does not apply, and
// "No license" when the customer holds none — the freeloader state the
// license columns exist to surface. Everything else delegates to the license
// badge — countdown, overdue escalation, the Expired end stamp and the
// lifecycle hover included.
function LicenseGoverningBadge(props: LicenseGoverningBadgeProps) {
  const { license, statusName, ...badgeProps } = props

  if (license.LicenseStatusCode == null) return null

  if (license.LicenseStatusCode === LICENSE_STATUS_NONE) {
    return (
      <Badge variant="outline" iconSrc="circle-dashed" {...badgeProps}>
        No license
      </Badge>
    )
  }

  return (
    <LicenseStatusBadge
      license={{
        LicenseStatusCode: license.LicenseStatusCode,
        TrialEndsAt: license.LicenseTrialEndsAt,
        PlanName: license.LicensePlanNames,
        Seats: license.LicensedSeats,
        RenewsAt: license.LicenseRenewsAt,
        // The aggregate's end date plays the per-license CancelledAt role,
        // but ONLY while Expired governs: an Active customer's old expired
        // license must not surface a stale end date on a live badge.
        CancelledAt:
          license.LicenseStatusCode === LICENSE_STATUS_EXPIRED
            ? license.LicenseEndedAt
            : null,
        OwnerName: license.OwnerName,
      }}
      statusName={statusName}
      {...badgeProps}
    />
  )
}

export default LicenseGoverningBadge
